// Articulatory targets for the Pink Trombone engine (tract n=44, lips≈39-43).
// state = { ti, td, c: [[index, diameter]], velum, voice, tenseness }
// ti: tongue index 12 (back, velum) … 29 (front);  td: 2.05 (high/close) … 3.5 (low/open)
const LIPS = 41, LABIODENT = 40, DENTAL = 38, ALV = 36, POSTALV = 34, VELAR = 22;
const NASAL = 0.4;

const V = (ti, td, lips, tenseness = 0.6) => ({ ti, td, c: lips ? [[LIPS, lips]] : [], voice: true, tenseness, velum: null });
const C = (c, { voice = true, velum = null, ti, td, tenseness = 0.6 } = {}) => ({
  c, voice, velum, tenseness, ...(ti != null ? { ti, td } : {}),
});

// kind drives timing: v=vowel, vl=long vowel, d=diphthong, s=stop, a=affricate, f=fricative, n=nasal, x=approximant
export const PHONES = {
  'iː': { kind: 'vl', s: V(29, 2.1, null, 0.8) }, i: { kind: 'v', s: V(28.5, 2.2, null, 0.7) },
  'ɪ': { kind: 'v', s: V(27, 2.5) }, e: { kind: 'v', s: V(25.8, 2.8) }, 'ɛ': { kind: 'v', s: V(25.2, 2.95) },
  'æ': { kind: 'v', s: V(23.5, 3.25) }, a: { kind: 'v', s: V(22, 3.5) }, 'ʌ': { kind: 'v', s: V(18.5, 3.1) }, 'ɒ': { kind: 'v', s: V(13.5, 3.3, 1.05) },
  'ɑ': { kind: 'v', s: V(14, 3.4) }, 'ɑː': { kind: 'vl', s: V(14, 3.4, null, 0.7) }, 'ɔ': { kind: 'v', s: V(13.5, 3.05, 0.95) },
  'ɔː': { kind: 'vl', s: V(13, 2.9, 0.8, 0.7) }, 'ʊ': { kind: 'v', s: V(14, 2.5, 0.85, 0.55) },
  u: { kind: 'v', s: V(12.8, 2.2, 0.6, 0.75) }, 'uː': { kind: 'vl', s: V(12.6, 2.15, 0.55, 0.8) },
  'ə': { kind: 'v', s: V(20, 2.9, null, 0.5) }, 'ɜː': { kind: 'vl', s: V(20, 2.75, null, 0.65) },
  'ɝ': { kind: 'vl', s: { ...V(19.5, 2.7), c: [[33, 0.75], [LIPS, 1.0]] } },
  'ɚ': { kind: 'v', s: { ...V(20, 2.8, null, 0.5), c: [[33, 0.85]] } },
  'eɪ': { kind: 'd', from: 'e', to: 'ɪ' }, 'aɪ': { kind: 'd', from: 'æ', to: 'ɪ', fromS: V(21, 3.35) },
  'ɔɪ': { kind: 'd', from: 'ɔ', to: 'ɪ' }, 'aʊ': { kind: 'd', from: 'æ', to: 'ʊ', fromS: V(20, 3.35) },
  'əʊ': { kind: 'd', from: 'ə', to: 'ʊ' }, 'oʊ': { kind: 'd', from: 'ɔ', to: 'ʊ', fromS: V(14, 2.75, 0.9) },
  'ɪə': { kind: 'd', from: 'ɪ', to: 'ə' }, 'eə': { kind: 'd', from: 'ɛ', to: 'ə' }, 'ʊə': { kind: 'd', from: 'ʊ', to: 'ə' },
  p: { kind: 's', s: C([[LIPS, 0]], { voice: false }) }, b: { kind: 's', s: C([[LIPS, 0]]) },
  t: { kind: 's', s: C([[ALV, 0]], { voice: false }) }, d: { kind: 's', s: C([[ALV, 0]]) },
  k: { kind: 's', s: C([[VELAR, 0]], { voice: false, ti: 15, td: 2.3 }) }, 'ɡ': { kind: 's', s: C([[VELAR, 0]], { ti: 15, td: 2.3 }) },
  'tʃ': { kind: 'a', s: C([[POSTALV, 0]], { voice: false }), rel: C([[POSTALV, 0.3], [LIPS, 0.9]], { voice: false }) },
  'dʒ': { kind: 'a', s: C([[POSTALV, 0]]), rel: C([[POSTALV, 0.3], [LIPS, 0.9]]) },
  f: { kind: 'f', s: C([[LABIODENT, 0.2]], { voice: false }) }, v: { kind: 'f', s: C([[LABIODENT, 0.25]]) },
  'θ': { kind: 'f', s: C([[DENTAL, 0.3]], { voice: false }) }, 'ð': { kind: 'f', s: C([[DENTAL, 0.35]]) },
  s: { kind: 'f', s: C([[ALV, 0.22]], { voice: false }) }, z: { kind: 'f', s: C([[ALV, 0.25]]) },
  'ʃ': { kind: 'f', s: C([[POSTALV, 0.3], [LIPS, 0.9]], { voice: false }) }, 'ʒ': { kind: 'f', s: C([[POSTALV, 0.32], [LIPS, 0.9]]) },
  h: { kind: 'f', s: C([], { voice: false, tenseness: 0.1 }) },
  m: { kind: 'n', s: C([[LIPS, 0]], { velum: NASAL }) }, n: { kind: 'n', s: C([[ALV, 0]], { velum: NASAL }) },
  'ŋ': { kind: 'n', s: C([[VELAR, 0]], { velum: NASAL, ti: 15, td: 2.3 }) },
  l: { kind: 'x', s: C([[35, 0.55]]) }, r: { kind: 'x', s: C([[33, 0.7], [LIPS, 0.95]], { ti: 20, td: 2.8 }) },
  j: { kind: 'x', s: C([[30, 0.6]], { ti: 29, td: 2.05 }) }, w: { kind: 'x', s: C([[LIPS, 0.45]], { ti: 13, td: 2.2 }) },
};

const DUR = { v: 170, vl: 260, f: 150, n: 110, x: 90 };
const stateOf = (sym) => PHONES[sym]?.s || PHONES[PHONES[sym]?.from]?.s;

// Expand a phoneme list into timed steps for pt:play.
export const toSteps = (phones, { rate = 1, f0 = 120 } = {}) => {
  const steps = [];
  const k = 1 / rate;
  phones.forEach((sym) => {
    const p = PHONES[sym];
    if (!p) return;
    const add = (state, dur, label) => steps.push({ state: { frequency: f0, ...state }, dur: dur * k, label: label || sym });
    if (p.kind === 'd') {
      add(p.fromS || PHONES[p.from].s, 150, sym);
      add(PHONES[p.to].s, 170, sym);
    } else if (p.kind === 's') {
      add(p.s, 75);
      add({ ...p.s, c: p.s.c.map(([i]) => [i, 0.4]) }, 30);
      if (!p.s.voice) add({ ...p.s, c: [], tenseness: 0.15 }, 35);
    } else if (p.kind === 'a') {
      add(p.s, 70);
      add(p.rel, 110);
    } else {
      add(p.s, DUR[p.kind]);
    }
  });
  return steps;
};

export const postureOf = (sym) => {
  const p = PHONES[sym];
  if (!p) return null;
  const s = p.kind === 'd' ? (p.fromS || PHONES[p.from].s) : p.s;
  return { ti: 20, td: 2.9, tenseness: 0.6, ...s };
};

export const isGlide = (sym) => PHONES[sym]?.kind === 'd';
export const sustainable = (sym) => ['v', 'vl', 'd', 'f', 'n', 'x'].includes(PHONES[sym]?.kind);

// Card id → main phoneme symbol (cards store IPA without slashes).
export const symbolForCard = (ipa = '') => {
  const s = ipa.replace(/[/]/g, '').trim();
  if (PHONES[s]) return s;
  const short = s.replace(/[ː:]/g, '');
  if (PHONES[short]) return short;
  return PHONES[`${short}ː`] ? `${short}ː` : null;
};

export { stateOf };
